import { Autocomplete } from '@mui/material';
import debounce from 'shell/utils/debounce';
import { memo, useCallback, useEffect, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';

interface AutocompleteListaSerieProps {
  disabled?: boolean;
  required?: boolean;
  value: ItemVM | null;
  onChange: (value?: ItemVM) => void;
  error?: boolean;
  isSmallSize?: boolean;
  label?: string;
  codUfficio?: string;
}

const AutocompleteListaSerieComponent = ({
  disabled,
  required,
  onChange,
  value,
  error,
  label,
  codUfficio,
}: AutocompleteListaSerieProps): React.ReactElement => {
  const [serie, getSerie] = useHttpFetch<HttpResponseList<string>, Array<ItemVM>>(
    `${__PRACTICE_API_URL__}/pratiche/ufficio-serie-locazioni/codiciSerie`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        abortable: true,
        method: 'GET',
        loading: false,
      },
      transformer: (response) =>
        response.data.payload.list.map((item) => ({
          id: item,
          value: item,
        })) as Array<ItemVM>,
    }
  );

  const serieData: Array<ItemVM> = useMemo(() => {
    return (serie?.data || []) as Array<ItemVM>;
  }, [serie?.data]);

  const defaultProps = useMemo(() => {
    return {
      options: (serie.data as Array<ItemVM>) || [],
      getOptionLabel: (option: ItemVM) => option.value,
    };
  }, [serie.data]);

  useEffect(() => {
    if (!codUfficio) {
      // eslint-disable-next-line no-console
      console.warn('nessun codice ufficio');
      return;
    }
    handleSearch(codUfficio);
  }, [codUfficio]);

  const handleSearch = debounce<string>(
    useCallback(
      (searchValue) => {
        if (serie.abort) {
          serie.abort();
        }

        const transformedFilters: OperationObject = {
          codUfficio: { value: searchValue || '', operation: Operation.LIKE },
        };

        const queryString = `${paginationListToQueryString({
          filters: transformedFilters,
          orders: {},
        })}`;

        getSerie({
          params: {},
          paramsSerializer: () => queryString,
        });
      },
      [getSerie, serie.abort]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={`${label}-id`}
      value={value}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(serieData.find((item) => item.id === element?.id));
      }}
      disabled={disabled}
      // onInputChange={(event, changeValue, reason) => {
      //   if (!changeValue) {
      //     return;
      //   }
      //   if (reason === 'input') {
      //     handleSearch(changeValue);
      //   }
      // }}
      clearOnBlur={false}
      loading={serie.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

AutocompleteListaSerieComponent.displayName = 'Autocomplete list serie component';

const AutocompleteListaSerie = memo(AutocompleteListaSerieComponent);

export default AutocompleteListaSerie as typeof AutocompleteListaSerieComponent;
